<template>
  <dialog
    ref="dialog"
    class="catalog-dialog"
    aria-labelledby="catalog-artist-title"
    @cancel="cancel"
    @click="backdrop"
  >
    <header class="catalog-dialog-header">
      <p class="workspace-eyebrow">
        {{ $t('catalog.artist-profile') }}
      </p><button
        class="catalog-close"
        :disabled="busy"
        :aria-label="$t('general.close')"
        @click="$emit('close')"
      >
        <GrowthIcon name="close" />
      </button>
    </header>
    <div class="catalog-dialog-profile">
      <div
        class="catalog-dialog-portrait"
        :data-tone="artistTone(artist)"
      >
        <img
          v-if="photo && !imageFailed"
          :src="photo"
          :alt="artist.name"
          @error="imageFailed = true"
        ><span
          v-else
          class="catalog-monogram"
          aria-hidden="true"
        >{{ artistInitials(artist.name) }}<GrowthIcon name="spark" /></span>
      </div>
      <div class="catalog-dialog-identity">
        <span class="catalog-genre-label">{{ artist.main_genre || $t('discovery.independent') }}</span><h2 id="catalog-artist-title">
          {{ artist.name }}
        </h2><CountryLabel
          v-if="artist.user?.country || artist.country"
          :value="artist.user?.country || artist.country"
        /><p
          v-if="artist.style"
          class="catalog-dialog-style"
        >
          {{ artist.style }}
        </p><p
          v-if="followerCount !== null"
          class="catalog-dialog-followers"
        >
          <strong>{{ n(followerCount) }}</strong> {{ $t('general.followers') }}
        </p>
      </div>
    </div>
    <p
      v-if="artist.bio"
      class="catalog-artist-bio"
    >
      {{ artist.bio }}
    </p>
    <div
      v-if="links.length"
      class="catalog-social-links"
    >
      <a
        v-for="link in links"
        :key="link.label"
        :href="link.url"
        target="_blank"
        rel="noopener noreferrer"
      >{{ link.label === 'website' ? $t('toolStudio.social-website') : link.label }}<GrowthIcon name="up" /></a>
    </div>
    <div class="catalog-dialog-actions">
      <a
        v-if="profileUrl"
        :href="profileUrl"
        class="growth-button growth-button--lime"
        target="_blank"
        rel="noopener noreferrer"
      >{{ $t('catalog.open-page') }}<GrowthIcon name="up" /></a>
      <router-link
        v-if="!user"
        :to="loginLink"
        class="growth-button"
      >
        {{ $t('catalog.sign-in-follow') }}<GrowthIcon name="plus" />
      </router-link>
      <button
        v-else-if="!isOwnArtist"
        class="button is-light"
        :class="{ 'is-loading': busy || checking }"
        :disabled="busy || checking || !followReady"
        :aria-pressed="following"
        @click="$emit('toggle-follow')"
      >
        <GrowthIcon :name="following ? 'check' : 'plus'" />{{ following ? $t('feed.following.unfollow') : $t('feed.follow-artist') }}
      </button>
      <span
        v-else
        class="catalog-your-profile"
      ><GrowthIcon name="check" />{{ $t('catalog.your-profile') }}</span>
    </div>
    <p
      v-if="!isOwnArtist && !following && !error"
      class="catalog-follow-hint"
    >
      {{ $t('catalog.follow-copy') }}
    </p>
    <div
      v-if="error"
      class="product-message"
      role="alert"
    >
      <p>{{ $t(followReady ? 'feed.follow-error' : 'catalog.follow-check-error') }}</p><button
        :disabled="busy || checking"
        @click="$emit(followReady ? 'toggle-follow' : 'retry-status')"
      >
        {{ $t('general.retry') }}
      </button>
    </div>
    <p
      v-if="success"
      class="catalog-follow-success"
      role="status"
    >
      <GrowthIcon name="check" />{{ $t(following ? 'feed.followed-artist' : 'feed.unfollowed-artist', { name: artist.name }) }}
    </p>
  </dialog>
</template>
<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import GrowthIcon from '@/components/growth/GrowthIcon.vue';
import CountryLabel from '@/components/common/CountryLabel.vue';
import { artistInitials, artistTone, artistLinks, artistProfileUrl } from '@/utils/artistCatalog';
const props = defineProps({ artist: { type: Object, required: true }, user: { type: Object, default: null }, isOwnArtist: Boolean, following: Boolean, busy: Boolean, checking: Boolean, followReady: Boolean, error: Boolean, success: Boolean, loginLink: { type: Object, required: true } });
const emit = defineEmits(['close', 'toggle-follow', 'retry-status']);
const { n } = useI18n();
const dialog = ref(null);
const imageFailed = ref(false);
const photo = computed(() => props.artist.cover_image || props.artist.cover_image_small);
const profileUrl = computed(() => artistProfileUrl(props.artist));
const links = computed(() => artistLinks(props.artist));
const followerCount = computed(() => props.artist.followers !== undefined && props.artist.followers !== null && Number.isFinite(Number(props.artist.followers)) ? Math.max(0, Number(props.artist.followers)) : null);
function cancel(event) { if (props.busy) event.preventDefault(); else emit('close'); }
function backdrop(event) { if (event.target !== dialog.value || props.busy) return; const r = dialog.value.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) emit('close'); }
onMounted(() => dialog.value.showModal());
onBeforeUnmount(() => dialog.value?.close());
</script>
